import { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, FormControl, InputLabel,
  Tooltip, Pagination, Box, Typography, Menu,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Stack, Divider, Avatar, IconButton, CircularProgress
} from "@mui/material";
import {
  Search, FilterList, Download, CheckCircle, Email, Person,
  ArrowUpward, ArrowDownward, SwapVert, KeyboardArrowDown,
  FactCheck, Close, Check, Cancel
} from "@mui/icons-material";
import { BASE_URL } from "../services/configUrls";


const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];
const IGP_GRADES = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "F"];
const YEAR_OPTIONS = ["2026", "2025", "2024", "2023"];

const gradeColor = (g) => {
  if (!g) return { color: "#6b7280", bg: "#f3f4f6" };
  const upper = g.toUpperCase();
  if (["A+", "A", "O", "E"].some(x => upper.startsWith(x))) return { color: "#166534", bg: "#dcfce7" };
  if (["B+", "B"].some(x => upper.startsWith(x)))           return { color: "#1e40af", bg: "#dbeafe" };
  if (["C+", "C"].some(x => upper.startsWith(x)))           return { color: "#92400e", bg: "#fef3c7" };
  return { color: "#991b1b", bg: "#fee2e2" };
};

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "??";

const avatarBg = (name) => {
  const palette = ["#7c3aed","#0369a1","#0f766e","#b45309","#be123c","#15803d","#c2410c","#6d28d9"];
  let h = 0;
  for (const c of (name || "")) h = (h * 31 + c.charCodeAt(0)) % palette.length;
  return palette[h];
};

const buildWeeksArray = (weekNumber) => {
  const completed = weekNumber
    ? parseInt(weekNumber.toString().replace(/\D/g, ""))
    : 0;
  return Array.from({ length: 8 }, (_, i) => {
    if (i < completed) return "done";
    if (i === completed) return "ongoing";
    return "empty";
  });
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const SelectLoader = (props) => (
  <Box className={props.className} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
    <CircularProgress size={16} sx={{ color: "text.disabled" }} />
  </Box>
);


// ── Cell Components ───────────────────────────────────────────────────────────

function WeekCell({ value }) {
  if (value === "done")
    return (
      <TableCell align="center" sx={{ px: 0.5, width: 28 }}>
        <Tooltip title="Completed">
          <CheckCircle sx={{ fontSize: "0.9rem", color: "#16a34a", display: "block", mx: "auto" }} />
        </Tooltip>
      </TableCell>
    );
  if (value === "ongoing")
    return (
      <TableCell align="center" sx={{ px: 0.5, width: 28 }}>
        <Tooltip title="Ongoing">
          <CheckCircle sx={{ fontSize: "0.9rem", color: "#f97316", display: "block", mx: "auto" }} />
        </Tooltip>
      </TableCell>
    );
  return (
    <TableCell align="center" sx={{ color: "text.disabled", px: 0.5, width: 28 }}>—</TableCell>
  );
}

function OfferLetterCell({ userId, internshipId, isAccept, isIssue }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const endpoint =
        isIssue > 0
          ? `${BASE_URL}/cohort-offer-letters/download-issued-offer-letter/${userId}/${internshipId}`
          : `${BASE_URL}/cohort-offer-letters/download-offer-letter/${userId}/${internshipId}`;
      const res = await fetch(endpoint, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403)      alert("Offer letter not yet issued.");
        else if (res.status === 404) alert(err?.detail || "Not found.");
        else                         alert(err?.detail || `Failed: ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      const disposition = res.headers.get("Content-Disposition");
      const match       = disposition?.match(/filename=([^;]+)/);
      a.download = match ? match[1].trim() : `OfferLetter_${internshipId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <TableCell align="center" sx={{ px: 0.5 }}>
      <Tooltip title={isAccept ? "Download Offer Letter" : "Not accepted"} placement="top">
        <span>
          <IconButton
            size="small"
            disabled={!isAccept || downloading}
            onClick={handleDownload}
            sx={{
              p: 0.4,
              color: isAccept ? "#7c3aed" : "text.disabled",
              "&:hover": isAccept ? { bgcolor: "#f5f3ff" } : {},
            }}
          >
            {downloading
              ? <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
              : <Download sx={{ fontSize: "1rem" }} />
            }
          </IconButton>
        </span>
      </Tooltip>
    </TableCell>
  );
}

function IgpCell({ igp }) {
  if (!igp)
    return <TableCell align="center" sx={{ color: "text.disabled", px: 0.5 }}>—</TableCell>;
  const gc = gradeColor(igp);
  return (
    <TableCell align="center" sx={{ px: 0.5 }}>
      <Box sx={{
        px: 0.75, py: 0.2, borderRadius: 1,
        bgcolor: gc.bg, color: gc.color,
        fontSize: "0.68rem", fontWeight: 700,
        display: "inline-block", whiteSpace: "nowrap",
      }}>
        {igp}
      </Box>
    </TableCell>
  );
}

function PercentCell({ percent }) {
  if (percent == null)
    return <TableCell align="center" sx={{ color: "text.disabled", px: 0.5 }}>—</TableCell>;
  return (
    <TableCell align="center" sx={{ px: 0.5 }}>
      <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", fontWeight: 500 }}>
        {Number(percent).toFixed(1)}%
      </Typography>
    </TableCell>
  );
}

function CertCell({ cert }) {
  const isValid = cert && !cert.endsWith("/0/0");
  if (!isValid) return <TableCell sx={{ px: 0.5 }} />;
  const href = cert?.startsWith("http://") || cert?.startsWith("https://") || cert?.startsWith("/")
    ? cert
    : `https://${cert}`;
  return (
    <TableCell align="center" sx={{ px: 0.5 }}>
      <Tooltip title="Download Certificate" placement="top">
        <IconButton
          size="small"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ p: 0.4, color: "#2563eb", "&:hover": { bgcolor: "#eff6ff" } }}
        >
          <Download sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </TableCell>
  );
}

const TH = ({ children, width, align = "left", sx: extraSx = {} }) => (
  <TableCell
    align={align}
    sx={{
      fontWeight: 700, fontSize: "0.65rem", color: "text.secondary",
      textTransform: "uppercase", letterSpacing: 0.3,
      py: 1, px: 0.5, whiteSpace: "nowrap", lineHeight: 1.2,
      ...(width ? { width } : {}),
      ...extraSx,
    }}
  >
    {children}
  </TableCell>
);

function NameSortTH({ sortOrder, onSort, width }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick  = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const handleClose  = () => setAnchorEl(null);
  const handleSelect = (value) => { onSort(value); handleClose(); };

  return (
    <>
      <TableCell sx={{
        fontWeight: 700, fontSize: "0.65rem", color: "text.secondary",
        textTransform: "uppercase", letterSpacing: 0.3,
        py: 1, px: 0.5, whiteSpace: "nowrap", userSelect: "none",
        ...(width ? { width } : {}),
      }}>
        <Stack direction="row" alignItems="center" gap={0.3}>
          Name &amp; Email
          <IconButton size="small" onClick={handleClick} sx={{
            p: 0.2,
            color: sortOrder ? "primary.main" : "text.disabled",
            "&:hover": { bgcolor: "#f5f3ff" },
          }}>
            <KeyboardArrowDown sx={{
              fontSize: "1.2rem",
              color: sortOrder ? "primary.main" : "text.disabled",
              transition: "transform 0.25s ease",
              transform: open
                ? "rotate(180deg) scale(1.35)"
                : sortOrder === "asc"
                ? "rotate(180deg)"
                : "rotate(0deg)",
            }} />
          </IconButton>
        </Stack>
      </TableCell>

      <Menu
        anchorEl={anchorEl} open={open} onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 140, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" } }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <MenuItem selected={sortOrder === "asc"} onClick={() => handleSelect("asc")} sx={{ fontSize: "0.78rem", gap: 1 }}>
          <ArrowUpward sx={{ fontSize: "0.85rem", color: "#7c3aed" }} /> A → Z
        </MenuItem>
        <MenuItem selected={sortOrder === "desc"} onClick={() => handleSelect("desc")} sx={{ fontSize: "0.78rem", gap: 1 }}>
          <ArrowDownward sx={{ fontSize: "0.85rem", color: "#7c3aed" }} /> Z → A
        </MenuItem>
        {sortOrder && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => handleSelect("")} sx={{ fontSize: "0.78rem", gap: 1, color: "text.secondary" }}>
              <SwapVert sx={{ fontSize: "0.85rem" }} /> Clear Sort
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}

const TH2 = ({ line1, line2, width, align = "center" }) => (
  <TableCell align={align} sx={{
    fontWeight: 700, fontSize: "0.65rem", color: "text.secondary",
    textTransform: "uppercase", letterSpacing: 0.3,
    py: 0.75, px: 0.5, lineHeight: 1.2,
    ...(width ? { width } : {}),
  }}>
    <Box>{line1}</Box>
    <Box>{line2}</Box>
  </TableCell>
);


// ── Main Component ────────────────────────────────────────────────────────────

export default function Gradebook() {

  const [apiData, setApiData]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [apiError, setApiError] = useState("");

  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // DSPOC context
  const [isDspoc,     setIsDspoc]     = useState(false);
  const [dspocBranch, setDspocBranch] = useState(null);
  const [dspocReady,  setDspocReady]  = useState(false); // true once role is known

  const [allDomains,  setAllDomains]  = useState([]);
  const [allCohorts,  setAllCohorts]  = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [allBranches, setAllBranches] = useState([]);

  const [searchFilter,  setSearchFilter]  = useState("");
  const [domainFilter,  setDomainFilter]  = useState("");
  const [cohortFilter,  setCohortFilter]  = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [branchFilter,  setBranchFilter]  = useState("");
  const [sortOrder,     setSortOrder]     = useState("");

  const [page,        setPage]        = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [pendingSearch,  setPendingSearch]  = useState("");
  const [pendingDomain,  setPendingDomain]  = useState("");
  const [pendingCohort,  setPendingCohort]  = useState("");
  const [pendingProgram, setPendingProgram] = useState("");
  const [pendingBranch,  setPendingBranch]  = useState("");
  const [pendingYear,    setPendingYear]    = useState("2026");

  const [exporting, setExporting] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName,    setNewName]    = useState("");
  const [newEmail,   setNewEmail]   = useState("");
  const [newCohort,  setNewCohort]  = useState("15");
  const [newIgp,     setNewIgp]     = useState("B");
  const [formError,  setFormError]  = useState("");

  // Certificates dialog state
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [internCerts, setInternCerts] = useState([]);
  const [certLoading, setCertLoading] = useState(false);
  const [certError, setCertError] = useState("");
  const [certSubmitting, setCertSubmitting] = useState(false);
  
  // PDF Viewer Dialog state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Verification confirmation state
  const [verifyWarningOpen, setVerifyWarningOpen] = useState(false);
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [pendingVerifyCourseId, setPendingVerifyCourseId] = useState(null);

  const handleViewS3File = async (url) => {
    if (!url) return;
    
    setPdfDialogOpen(true);
    setPdfLoading(true);
    setPdfUrl("");
    
    try {
      if (!url.includes("s3.amazonaws.com")) {
        setPdfUrl(url);
        setPdfLoading(false);
        return;
      }
      
      const urlObj = new URL(url);
      const fileKey = decodeURIComponent(urlObj.pathname.substring(1)); // remove leading '/' and decode

      const res = await fetch(`${BASE_URL}/get-image-from-s3`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ file_key: fileKey })
      });
      
      if (!res.ok) throw new Error("Failed to get secure URL");
      const data = await res.json();
      
      setPdfUrl(data.url);
    } catch (error) {
      console.error("Error fetching secure URL:", error);
      // Fallback to original URL
      setPdfUrl(url);
    } finally {
      setPdfLoading(false);
    }
  };
  const handleOpenCerts = async (intern) => {
    setSelectedIntern(intern);
    setCertDialogOpen(true);
    setCertLoading(true);
    setCertError("");
    try {
      const res = await fetch(`${BASE_URL}/institute/tracker/intern/${intern.internship_id}/certificates`, { headers: getAuthHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || "Failed to fetch certificates");
      }
      const data = await res.json();
      setInternCerts(data);
    } catch (err) {
      setCertError(err.message);
    } finally {
      setCertLoading(false);
    }
  };

  const handleCloseCerts = () => {
    setCertDialogOpen(false);
    setSelectedIntern(null);
    setInternCerts([]);
  };

  const verifyCertificate = async (course_id, action, reason = null) => {
    if (action === "reject" && !reason) {
      alert("Please provide a reason for rejection.");
      return;
    }
    setCertSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/institute/tracker/intern/${selectedIntern.internship_id}/certificates/${course_id}/verify`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ action, reason })
      });
      
      if (!res.ok) {
         const err = await res.json().catch(() => ({}));
         throw new Error(err.detail || "Failed to update certificate");
      }
      // Refresh the list after success
      const resRefresh = await fetch(`${BASE_URL}/institute/tracker/intern/${selectedIntern.internship_id}/certificates`, { headers: getAuthHeaders() });
      if (resRefresh.ok) {
        setInternCerts(await resRefresh.json());
      }
    } catch(err) {
      alert("Error: " + err.message);
    } finally {
      setCertSubmitting(false);
    }
  };

  const rows       = apiData?.items      || [];
  const total      = apiData?.total      || 0;
  const totalPages = apiData?.total_pages || 1;


  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchDomains = async (overrideCohort) => {
    setLoadingDomains(true);
    try {
      const params  = new URLSearchParams();
      const cohortQ = overrideCohort !== undefined ? overrideCohort : cohortFilter;
      if (cohortQ) params.set("cohort_id", cohortQ);
      const res = await fetch(`${BASE_URL}/institute/tracker/domains?${params}`, { headers: getAuthHeaders() });
      if (res.ok) setAllDomains(await res.json());
    } catch { /* silently fail */ }
    finally { setLoadingDomains(false); }
  };

  const fetchCohorts = async (year) => {
    setLoadingCohorts(true);
    try {
      const params = new URLSearchParams();
      if (year) params.set("year", year);
      const res = await fetch(
        `${BASE_URL}/institute/tracker/cohorts?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setAllCohorts(data);
        return data;
      }
      return [];
    } catch { return []; }
    finally { setLoadingCohorts(false); }
  };

  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const res = await fetch(`${BASE_URL}/institute/tracker/programs`, { headers: getAuthHeaders() });
      if (res.ok) setAllPrograms(await res.json());
    } catch { /* silently fail */ }
    finally { setLoadingPrograms(false); }
  };

  const fetchBranches = async (overrideProgram) => {
    setLoadingBranches(true);
    try {
      const params   = new URLSearchParams();
      const programQ = overrideProgram !== undefined ? overrideProgram : pendingProgram;
      if (!programQ) { setAllBranches([]); return; }
      params.set("program_id", programQ);
      const res = await fetch(`${BASE_URL}/institute/tracker/branches?${params}`, { headers: getAuthHeaders() });
      if (res.ok) setAllBranches(await res.json());
    } catch { /* silently fail */ }
    finally { setLoadingBranches(false); }
  };

  const fetchData = async (overrides = {}) => {
    setLoading(true);
    setApiError("");
    try {
      const params = new URLSearchParams();
      const p        = overrides.page        ?? page;
      const ps       = overrides.rowsPerPage ?? rowsPerPage;
      const emailQ   = overrides.search      !== undefined ? overrides.search    : searchFilter;
      const domainQ  = overrides.domain      !== undefined ? overrides.domain    : domainFilter;
      const cohortQ  = overrides.cohort      !== undefined ? overrides.cohort    : cohortFilter;
      const programQ = overrides.program     !== undefined ? overrides.program   : programFilter;
      const branchQ  = overrides.branch      !== undefined ? overrides.branch    : branchFilter;
      const sortQ    = overrides.sortOrder   !== undefined ? overrides.sortOrder : sortOrder;

      params.set("page",      p);
      params.set("page_size", ps);
      if (emailQ)   params.set("name",       emailQ);
      if (domainQ)  params.set("domain_id",  domainQ);
      if (cohortQ)  params.set("cohort_id",  cohortQ);
      if (programQ) params.set("program_id", programQ);
      if (branchQ)  params.set("branch_id",  branchQ);
      if (sortQ) {
        params.set("sort_by",    "name");
        params.set("sort_order", sortQ);
      }

      const res = await fetch(`${BASE_URL}/institute/tracker?${params}`, { headers: getAuthHeaders() });
      const contentType = res.headers.get("content-type");
      if (!contentType?.includes("application/json"))
        throw new Error(`Expected JSON but got HTML. Status: ${res.status}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData?.detail || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setApiData(data);

      // Pick up DSPOC context from API response
      if (data.is_dspoc !== undefined) setIsDspoc(data.is_dspoc);
      if (data.dspoc_branch !== undefined) setDspocBranch(data.dspoc_branch);
      setDspocReady(true);

    } catch (err) {
      setApiError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (cohortFilter)  params.set("cohort_id",  cohortFilter);
      if (domainFilter)  params.set("domain_id",  domainFilter);
      if (programFilter) params.set("program_id", programFilter);
      if (branchFilter)  params.set("branch_id",  branchFilter);
      if (searchFilter)  params.set("name",        searchFilter);

      const res  = await fetch(`${BASE_URL}/institute/tracker/export?${params}`, { method: "POST", headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Export failed");
      alert(`✅ Export sent to ${data.sent_to}`);
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  // ── Detect DSPOC role on mount (lightweight call, page_size=1) ────────────
  const detectRole = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/institute/tracker?page=1&page_size=1`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const is_dspoc     = data.is_dspoc     ?? false;
        const dspoc_branch = data.dspoc_branch ?? null;
        setIsDspoc(is_dspoc);
        setDspocBranch(dspoc_branch);
        setDspocReady(true);
        return { is_dspoc, dspoc_branch };
      }
    } catch { /* silently fail */ }
    setDspocReady(true);
    return { is_dspoc: false, dspoc_branch: null };
  };

  // ── Event handlers ────────────────────────────────────────────────────────

  const handleSortToggle = (value) => {
    setSortOrder(value);
    if (cohortFilter) {
      setPage(1);
      fetchData({
        search: searchFilter, domain: domainFilter, cohort: cohortFilter,
        program: programFilter, branch: branchFilter,
        sortOrder: value, page: 1,
      });
    }
  };

  const handlePendingProgram = (value) => {
    setPendingProgram(value);
    setPendingBranch("");
    fetchBranches(value);
  };

  const handlePendingCohort = (value) => {
    setPendingCohort(value);
    setPendingDomain("");
    fetchDomains(value);
    if (!dspocReady) {
      detectRole();
    }
  };

  const handleYearChange = (value) => {
    setPendingYear(value);
    setPendingCohort("");
    setPendingDomain("");
    setAllCohorts([]);
    fetchCohorts(value);
  };

  const handleApply = () => {
    if (!pendingCohort) return;
    setSearchFilter(pendingSearch);
    setDomainFilter(pendingDomain);
    setCohortFilter(pendingCohort);
    setProgramFilter(pendingProgram);
    setBranchFilter(pendingBranch);
    setSortOrder("");
    setPage(1);
    fetchData({
      search:    pendingSearch,
      domain:    pendingDomain,
      cohort:    pendingCohort,
      program:   pendingProgram,
      branch:    pendingBranch,
      sortOrder: "",
      page:      1,
    });
  };

  const clearFilters = async () => {
    setPendingSearch(""); setPendingDomain(""); setPendingProgram("");
    setPendingBranch(""); setPendingYear("2026");
    setSearchFilter(""); setDomainFilter(""); setCohortFilter("");
    setProgramFilter(""); setBranchFilter(""); setSortOrder("");
    setPage(1); setAllBranches([]); setApiData(null); setAllCohorts([]);

    const data = await fetchCohorts("2026");
    if (data && data.length > 0) {
      const firstId = String(data[0].cohort_id);
      setPendingCohort(firstId);
      fetchDomains(firstId);
      if (!dspocReady) detectRole();
    } else {
      setPendingCohort("");
    }
  };

  const handlePageChange = (_, v) => {
    if (!cohortFilter) return;
    setPage(v);
    fetchData({
      page:    v,
      search:  searchFilter,
      domain:  domainFilter,
      cohort:  cohortFilter,
      program: programFilter,
      branch:  branchFilter,
    });
  };

  const handleRowsPerPageChange = (e) => {
    if (!cohortFilter) return;
    const ps = Number(e.target.value);
    setRowsPerPage(ps);
    setPage(1);
    fetchData({
      rowsPerPage: ps,
      page:        1,
      search:      searchFilter,
      domain:      domainFilter,
      cohort:      cohortFilter,
      program:     programFilter,
      branch:      branchFilter,
    });
  };

  // ── On mount ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      // Run programs load in parallel
      fetchPrograms();

      // 1. Load cohorts first
      const data = await fetchCohorts("2026");
      if (data && data.length > 0) {
        const firstId = String(data[0].cohort_id);
        setPendingCohort(firstId);
        fetchDomains(firstId);
        detectRole();
      } else {
        detectRole();
      }
    };

    init();
  }, []);

  const selectedCohortObj = allCohorts.find(c => String(c.cohort_id) === String(pendingCohort));
  const isCohortOpen      = selectedCohortObj?.is_open === '1';

  const handleAdd = () => {
    if (!newName.trim())                      { setFormError("Full name is required"); return; }
    if (!newEmail || !newEmail.includes("@")) { setFormError("Enter a valid email address"); return; }
    setNewName(""); setNewEmail(""); setNewCohort("15"); setNewIgp("B");
    setFormError(""); setDialogOpen(false);
  };


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="h6" fontWeight={700} color="primary.main" letterSpacing={-0.5}>
            Gradebook
          </Typography>
          {/* DSPOC branch badge */}
          {dspocReady && isDspoc && dspocBranch && (
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              px: 1.2, py: 0.3, borderRadius: 2,
              bgcolor: "#f5f3ff", border: "1px solid #ede9fe",
            }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#7c3aed", fontWeight: 700 }}>
                Branch: {dspocBranch}
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>

      {/* API Error */}
      {apiError && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: "error.light", bgcolor: "#fff5f5" }}>
          <Typography variant="body2" color="error">{apiError}</Typography>
        </Paper>
      )}

      {/* ── Filters ── */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1.5, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" gap={0.5} mb={0.75}>
          <FilterList sx={{ fontSize: "0.8rem", color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}
            textTransform="uppercase" letterSpacing={0.5} sx={{ fontSize: "0.65rem" }}>
            Filters
          </Typography>
        </Stack>

        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search name…"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            sx={{ width: 155, "& .MuiInputBase-input": { fontSize: "0.75rem", py: 0.5 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: "0.85rem", color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Year */}
          <FormControl size="small" sx={{ width: 80 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Year</InputLabel>
            <Select value={pendingYear} label="Year"
              onChange={(e) => handleYearChange(e.target.value)}
              sx={{ fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}>
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: "0.75rem" }}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Cohort */}
          <FormControl size="small" sx={{ width: 115 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Cohort</InputLabel>
            <Select value={pendingCohort} label="Cohort"
              disabled={loadingCohorts}
              onChange={(e) => handlePendingCohort(e.target.value)}
              IconComponent={loadingCohorts ? SelectLoader : undefined}
              sx={{ fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {allCohorts.map((c) => (
                <MenuItem key={c.cohort_id} value={String(c.cohort_id)} sx={{ fontSize: "0.75rem" }}>
                  {c.cohort_name}
                  {c.is_open === '1' && (
                    <Typography component="span" sx={{ ml: 0.5, fontSize: "0.6rem", color: "success.main" }}>
                      ● open
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Domain */}
          <FormControl size="small" sx={{ width: 150 }}>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Domain</InputLabel>
            <Select value={pendingDomain} label="Domain"
              disabled={loadingDomains}
              onChange={(e) => setPendingDomain(e.target.value)}
              IconComponent={loadingDomains ? SelectLoader : undefined}
              sx={{ fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {allDomains.map((d) => (
                <MenuItem key={d.domain_id} value={String(d.domain_id)} sx={{ fontSize: "0.75rem" }}>
                  {d.domain_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Stream — hidden for DSPOC */}
          {(!dspocReady || !isDspoc) && (
            <FormControl size="small" sx={{ width: 130 }}>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Stream</InputLabel>
              <Select value={pendingProgram} label="Stream"
                disabled={loadingPrograms}
                onChange={(e) => handlePendingProgram(e.target.value)}
                IconComponent={loadingPrograms ? SelectLoader : undefined}
                sx={{ fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {allPrograms.map((p) => (
                  <MenuItem key={p.program_id} value={String(p.program_id)} sx={{ fontSize: "0.75rem" }}>
                    {p.program_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Branch — hidden for DSPOC (auto-applied from their role on backend) */}
          {(!dspocReady || !isDspoc) && (
            <FormControl size="small" sx={{ width: 150 }} disabled={!pendingProgram || loadingBranches}>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Branch</InputLabel>
              <Select value={pendingBranch} label="Branch"
                onChange={(e) => setPendingBranch(e.target.value)}
                IconComponent={loadingBranches ? SelectLoader : undefined}
                sx={{ fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {allBranches.map((b) => (
                  <MenuItem key={b.branch_id} value={String(b.branch_id)} sx={{ fontSize: "0.75rem" }}>
                    {b.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Go */}
          <Button
            size="small" variant="contained" onClick={handleApply}
            disabled={!pendingCohort} disableElevation
            sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 600, py: 0.55, minWidth: 44 }}>
            Go
          </Button>

          {/* Clear */}
          {(pendingSearch || pendingDomain || pendingCohort || pendingProgram || pendingBranch) && (
            <Button size="small" variant="text" color="error" onClick={clearFilters}
              sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0 }}>
              Clear
            </Button>
          )}

        </Stack>
      </Paper>

      {/* Count + legend + export */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="caption" color="text.secondary">
            {total} intern{total !== 1 ? "s" : ""}
          </Typography>
          {apiData && (
            <>
              <Stack direction="row" alignItems="center" gap={0.4}>
                <CheckCircle sx={{ fontSize: "0.78rem", color: "#16a34a" }} />
                <Typography variant="caption" sx={{ fontSize: "0.68rem" }} color="text.secondary">Completed</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={0.4}>
                <CheckCircle sx={{ fontSize: "0.78rem", color: "#f97316" }} />
                <Typography variant="caption" sx={{ fontSize: "0.68rem" }} color="text.secondary">Ongoing</Typography>
              </Stack>
            </>
          )}
        </Stack>

        <Tooltip title={isCohortOpen ? "Available only after cohort closes" : ""}>
          <span>
            <Button
              size="small" variant="outlined"
              startIcon={exporting ? <CircularProgress size={12} /> : <Download sx={{ fontSize: "0.8rem !important" }} />}
              disabled={!apiData || rows.length === 0 || isCohortOpen || exporting}
              onClick={handleExport} disableElevation
              sx={{ textTransform: "none", fontSize: "0.72rem", borderRadius: 1.5, py: 0.4 }}
            >
              {exporting ? "Sending…" : "Export"}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* ── Table ── */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <NameSortTH sortOrder={sortOrder} onSort={handleSortToggle} width="18%" />
              <TH width="14%">Domain</TH>
              <TH width="3%" align="center">W1</TH>
              <TH2 line1="Offer" line2="Letter" width="5%" align="center" />
              <TH width="3%" align="center">W2</TH>
              <TH width="3%" align="center">W3</TH>
              <TH width="3%" align="center">W4</TH>
              <TH width="3%" align="center">W5</TH>
              <TH width="3%" align="center">W6</TH>
              <TH width="3%" align="center">W7</TH>
              <TH width="3%" align="center">W8</TH>
              <TH2 line1="course" line2="Cert" width="4%" align="center" />
              <TH width="4%" align="center">Score</TH>
              <TH width="5%" align="center">Grade</TH>
              <TH2 line1="INT" line2="Cert" width="4%" align="center" />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 5, color: "text.disabled", border: 0 }}>
                  <CircularProgress size={22} sx={{ color: "#7c3aed" }} />
                </TableCell>
              </TableRow>
            ) : !apiData ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 8, color: "text.disabled", border: 0 }}>
                  Select a cohort and click Go to load data.
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 8, color: "text.disabled", border: 0 }}>
                  No records found.
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const weeks = buildWeeksArray(row.week_number);
              return (
                <TableRow key={row.internship_id} hover>

                  {/* Name + Email */}
                  <TableCell sx={{ px: 0.5 }}>
                    <Stack direction="row" alignItems="center" gap={0.8}>
                      <Avatar sx={{
                        width: 26, height: 26, fontSize: "0.62rem",
                        fontWeight: 700, bgcolor: avatarBg(row.name), flexShrink: 0,
                      }}>
                        {getInitials(row.name)}
                      </Avatar>
                      <Box sx={{ overflow: "hidden", minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}
                          sx={{ fontSize: "0.71rem", lineHeight: 1.3 }} noWrap>
                          {row.name?.trim() || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: "0.61rem" }} noWrap>
                          {row.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Domain */}
                  <TableCell sx={{ px: 0.5 }}>
                    <Typography variant="caption" sx={{
                      fontSize: "0.67rem", whiteSpace: "normal",
                      wordBreak: "break-word", lineHeight: 1.3,
                    }}>
                      {row.domain_name || "—"}
                    </Typography>
                  </TableCell>

                  {/* W1 */}
                  <WeekCell value={weeks[0]} />

                  {/* Offer Letter */}
                  <OfferLetterCell
                    userId={row.user_id}
                    internshipId={row.internship_id}
                    isAccept={row.is_accept}
                    isIssue={row.is_issue}
                  />

                  {/* W2 – W8 */}
                  {weeks.slice(1).map((w, i) => (
                    <WeekCell key={i + 1} value={w} />
                  ))}

                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Tooltip title="Verify Certificates" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCerts(row)}
                        sx={{ p: 0.4, color: "#10b981", "&:hover": { bgcolor: "#ecfdf5" } }}
                      >
                        <FactCheck sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <PercentCell percent={row.percent} />
                  <IgpCell     igp={row.igp_grade} />
                  <CertCell    cert={row.certificate_url} />

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems="center"
        justifyContent="space-between" mt={1.5} gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
          <Select size="small" value={rowsPerPage} onChange={handleRowsPerPageChange}
            sx={{ fontSize: "0.75rem", ".MuiSelect-select": { py: 0.4, px: 1 } }}>
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            {total === 0 ? "0" : `${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, total)}`} of {total}
          </Typography>
        </Stack>
        <Pagination count={totalPages} page={page} onChange={handlePageChange}
          color="primary" shape="rounded" size="small" showFirstButton showLastButton />
      </Stack>

      {/* ── Add Intern Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add New Intern</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {formError && (
            <Typography variant="caption" color="error" sx={{ mt: -0.5 }}>{formError}</Typography>
          )}
          <TextField autoFocus fullWidth size="small" label="Full Name" placeholder="e.g. John Doe"
            value={newName} onChange={(e) => { setNewName(e.target.value); setFormError(""); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: "1rem", color: "text.disabled" }} /></InputAdornment> }}
          />
          <TextField fullWidth size="small" label="Email Address" placeholder="e.g. john.2024@gmail.com"
            value={newEmail} onChange={(e) => { setNewEmail(e.target.value); setFormError(""); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: "1rem", color: "text.disabled" }} /></InputAdornment> }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Cohort</InputLabel>
            <Select value={newCohort} label="Cohort" onChange={(e) => setNewCohort(e.target.value)}>
              {allCohorts.map((c) => (
                <MenuItem key={c.cohort_id} value={String(c.cohort_id)}>{c.cohort_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>IGP Grade</InputLabel>
            <Select value={newIgp} label="IGP Grade" onChange={(e) => setNewIgp(e.target.value)}>
              {IGP_GRADES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }} disableElevation>
            Add Intern
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── Cert Verification Dialog ── */}
      <Dialog open={certDialogOpen} onClose={handleCloseCerts} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Verify Certificates
          <IconButton onClick={handleCloseCerts} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {selectedIntern && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="primary.main">
                {selectedIntern.name} ({selectedIntern.email})
              </Typography>
            </Box>
          )}

          {certLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : certError ? (
            <Typography color="error" variant="body2" sx={{ p: 2 }}>{certError}</Typography>
          ) : internCerts.length === 0 ? (
            <Typography color="text.secondary" variant="body2" sx={{ p: 2, textAlign: "center" }}>
              No certificates found or cohort is inactive.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Course</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align="center">Certificate</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {internCerts.map((cert) => (
                    <TableRow key={cert.course_id}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={500}>{cert.course_name}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{
                          px: 1.5, py: 0.4, borderRadius: 1.5, display: "inline-block", fontSize: "0.75rem",
                          fontWeight: 600,
                          bgcolor: cert.status === "Verified" ? "#dcfce7" : cert.status === "Pending" ? "#fef3c7" : cert.status === "Rejected" ? "#fee2e2" : "#f3f4f6",
                          color: cert.status === "Verified" ? "#166534" : cert.status === "Pending" ? "#92400e" : cert.status === "Rejected" ? "#991b1b" : "#4b5563"
                        }}>
                          {cert.status}
                        </Box>
                        {cert.reason && cert.status === "Rejected" && (
                          <Typography variant="caption" display="block" color="error" mt={0.5}>
                            Reason: {cert.reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {cert.verify_url ? (
                          <Button size="small" variant="outlined" onClick={() => handleViewS3File(cert.verify_url)}
                            sx={{ textTransform: 'none', borderRadius: 2, py: 0.2 }}>
                            View
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {cert.is_uploaded && cert.status !== "Verified" ? (
                          <Stack direction="row" gap={1} justifyContent="center">
                            <Button 
                              size="small" 
                              variant="contained" 
                              disableElevation
                              disabled={certSubmitting}
                              onClick={() => {
                                setPendingVerifyCourseId(cert.course_id);
                                setVerifyWarningOpen(true);
                              }}
                              sx={{ bgcolor: "#10b981", color: "#fff", textTransform: "none", minWidth: 0, "&:hover": { bgcolor: "#059669" } }}
                            >
                              Verify
                            </Button>
                            <Button 
                              size="small" 
                              variant="contained" 
                              disableElevation
                              disabled={certSubmitting}
                              onClick={() => {
                                const reason = prompt("Enter rejection reason:");
                                if (reason) verifyCertificate(cert.course_id, "reject", reason);
                              }}
                              sx={{ bgcolor: "#ef4444", color: "#fff", textTransform: "none", minWidth: 0, "&:hover": { bgcolor: "#dc2626" } }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        ) : cert.status === "Verified" ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* ── PDF Viewer Dialog ── */}
      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Document Viewer
          <IconButton onClick={() => setPdfDialogOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {pdfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress />
            </Box>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none', flexGrow: 1 }} title="Document Viewer" />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <Typography color="error">Failed to load document.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Verification Warning Dialog ── */}
      <Dialog open={verifyWarningOpen} onClose={() => setVerifyWarningOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, color: "warning.dark" }}>
          ⚠️ Certificate Verification Warning
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" paragraph fontWeight={500}>
            Please carefully verify the certificate details before approving.
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            Before clicking “Verify”, please check the following:
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, color: "text.secondary", fontSize: "0.875rem" }}>
            <li><strong>Certificate Name</strong> – Ensure the name mentioned on the certificate is correct.</li>
            <li><strong>Student Name</strong> – Ensure the certificate belongs to the correct student and the student name is correctly mentioned.</li>
          </Box>
          <Typography variant="body2" paragraph color="text.secondary">
            These checks are important to prevent incorrect certificates from being verified or approved for the wrong student.
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            By clicking “Verify”, you confirm that the certificate has been checked and is valid. The SPOC/Institute will be responsible for any incorrect or wrongful verification.
          </Typography>
          <Typography variant="body2" color="error.main" fontWeight={600}>
            Incorrect verification may result in withdrawal of the institute’s eligibility for future EduSkills Virtual Internship participation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setVerifyWarningOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => {
              setVerifyWarningOpen(false);
              setVerifyConfirmOpen(true);
            }}
          >
            I Understand & Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Verification Confirm Dialog ── */}
      <Dialog open={verifyConfirmOpen} onClose={() => setVerifyConfirmOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Verification</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1">
            Are you sure you want to verify this certificate?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setVerifyConfirmOpen(false)} sx={{ textTransform: "none" }}>
            No
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => {
              setVerifyConfirmOpen(false);
              verifyCertificate(pendingVerifyCourseId, "verify");
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}